import {
	AdditionalUserColour,
	BaseUserColour,
	Capability,
	ClusterAnalysis,
	EngineId,
	ExtraUserColour,
	LikedItem,
} from './commonTypes';

// These constants are used by both backend and frontend

export const ENGINE_TYPES = {
	CREATIVE: {
		id: 'Creative' as EngineId,
		name: 'Creative',
		fileAllowed: false,
	},
	INNOVATIVE: {
		id: 'Innovative' as EngineId,
		name: 'Innovative',
		fileAllowed: false,
	},
	GAMES: {
		id: 'Games' as EngineId,
		name: 'Experiential',
		fileAllowed: false,
	},
	CREATE: {
		id: 'Create' as EngineId,
		name: 'PDF-Create',
		fileAllowed: true,
	},
};

/** Phrase enrichment UI — keep in sync with phraseEnrichmentService window helpers. */
export const PHRASE_TOOL_UI = {
	SPARK_VISIBLE_COUNT: 6,
	SPARK_BUFFER_COUNT: 4,
	EMPATHISE_BUFFER_PER_LANE: 2,
	EMPATHISE_SECOND_PHRASE_SLIDER_THRESHOLD: 45,
	ONBOARDING_PHRASE_COUNT: 8,
	ONBOARDING_LLM_PHRASE_COUNT: 8,
	ONBOARDING_KG_PHRASE_COUNT: 0,
} as const;

/** Flowy spark coaching — suggest this many distinct phrases per results panel. */
export const FLOWY_SPARK_SUGGESTIONS = {
	MIN_COUNT: 2,
	MAX_COUNT: 4,
} as const;

/** Flowy empathise coaching — suggest this many distinct phrases per results panel. */
export const FLOWY_EMPATHISE_SUGGESTIONS = {
	MIN_COUNT: 2,
	MAX_COUNT: 3,
} as const;

/** Flowy brainstorm coaching — suggest this many distinct ideas per results panel. */
export const FLOWY_BRAINSTORM_SUGGESTIONS = {
	MIN_COUNT: 2,
	MAX_COUNT: 3,
} as const;

/** Chat messages longer than this get a collapsed preview with an Expand action. */
export const FLOWY_LONG_MESSAGE_CHARS = 400;

export type UseCase =
	| 'branding'
	| 'brainstorming'
	| 'copywriting'
	| 'design_thinking'
	| 'product_idea'
	| 'product_brief'
	| 'marketing'
	| 'default';

export const TOOLS = {
	SPARK: 'spark',
	EMPATHISE: 'empathise',
	BRAINSTORM: 'brainstorm',
	COMMON_IDEAS: 'commonIdeas',
	SEARCH: 'search',
	TAGLINES: 'taglines',
	HAIKUS: 'haikus',
	Ai_HAIKUS: 'aiHaikus',
	PERSONAS: 'personas',
	MOTIVATIONS: 'motivations',
	GENERATE: 'generate',
	SYNTHESISE: 'synthesise',
	IMAGE: 'image',
};

// Full roles are in the database as capabilities of each role may be changed dynamically
export const ROLE_ID = {
	FREE: 'restricted',
	PARTICIPANT: 'participant',
	FACILITATOR: 'facilitator',
	TRIAL: 'freemium',
	INDIVIDUAL: 'freelancer',
	TEAM: 'paid',
	STUDENT: 'student',
	TEACHER: 'teacher',
};

export type RoleId = (typeof ROLE_ID)[keyof typeof ROLE_ID];

export const CAPABILITIES: Array<Capability> = [
	{
		key: 'create-board',
		name: 'Create board',
		implemented: true,
		type: 'boolean',
	},
	{
		key: 'board-limit',
		name: 'Board limit',
		implemented: true,
		type: 'number',
	},
	{
		key: 'create-project',
		name: 'Create project',
		implemented: true,
		type: 'boolean',
	},
	{
		key: 'time-limited',
		name: 'Time-limited',
		implemented: true,
		type: 'boolean',
	},
	{
		key: 'collaborate-one',
		name: 'Collaborate (up to 2 users)',
		implemented: true,
		type: 'boolean',
	},
	{
		key: 'collaborate-two',
		name: 'Collaborate (up to 3 users)',
		implemented: true,
		type: 'boolean',
	},
	{
		key: 'collaborate-group',
		name: 'Collaborate (up to 4 users)',
		implemented: true,
		type: 'boolean',
	},
	{
		key: 'collaborate-max',
		name: 'Collaborate (up to 8 users)',
		implemented: true,
		type: 'boolean',
	},
	{
		key: 'export',
		name: 'Export',
		implemented: true,
		type: 'boolean',
	},
	{
		key: 'slides',
		name: 'Slides',
		implemented: true,
		type: 'boolean',
	},
	{
		key: 'advanced-ai',
		name: 'Advanced AI tools',
		implemented: true,
		type: 'boolean',
	},
	{
		key: 'image-quota',
		name: 'Image quota (monthly)',
		implemented: true,
		type: 'number',
	},
];

export type AnalysisKey =
	| 'Psych.cluster'
	| 'Archetype.cluster'
	| 'Plots.cluster'
	| 'colors.cluster'
	| 'Themes.cluster'
	| 'Random0.cluster'
	| 'Greek.cluster'
	| 'Motivations.cluster'
	| 'Motivation-Challenge.cluster'
	| 'Motivation-Harmony.cluster'
	| 'Motivation-Stimulation.cluster'
	| 'Motivations-Novelty.cluster'
	| 'Motivation-Threat.cluster'
	| 'Grounding.cluster';
export const ANALYSES_TYPES: Record<AnalysisKey, ClusterAnalysis> = {
	'Psych.cluster': { name: 'Personality', generate: ['persona', 'audience'] },
	'Archetype.cluster': { name: 'Archetype', generate: ['persona', 'audience'] },
	'Plots.cluster': { name: 'Plots', generate: ['persona'] },
	'colors.cluster': { name: 'Visual', generate: ['persona'] },
	'Themes.cluster': { name: 'Themes', generate: ['persona', 'motivations'] },
	'Random0.cluster': { name: 'Random', generate: ['persona', 'motivations'] },
	'Greek.cluster': { name: 'Greek Gods', generate: ['persona', 'audience'] },
	'Motivations.cluster': { name: 'Motivations', generate: ['motivations'] },
	'Motivation-Challenge.cluster': {
		name: 'Challenge',
		category: 'Motivations',
		generate: ['motivations'],
	},
	'Motivation-Harmony.cluster': {
		name: 'Harmony',
		category: 'Motivations',
		generate: ['motivations'],
	},
	'Motivation-Stimulation.cluster': {
		name: 'Stimulation',
		category: 'Motivations',
		generate: ['motivations'],
	},
	'Motivations-Novelty.cluster': {
		name: 'Novelty',
		category: 'Motivations',
		generate: ['motivations'],
	},
	'Motivation-Threat.cluster': {
		name: 'Threat',
		category: 'Motivations',
		generate: ['motivations'],
	},
	/** Foundations-only board view of captured grounding (not a psychographic lens). */
	'Grounding.cluster': { name: 'Grounding context', generate: [] },
};

export const userColours = ['orange', 'fuchsia', 'red', 'green'] as Array<BaseUserColour>;
export const userColoursAdditional = [
	'dark',
	'violet',
	'yellow',
	'cyan',
] as Array<AdditionalUserColour>;

export const userColoursExtra = [
	'blue',
	'pink',
	'lime',
	'teal',
	'maroon',
	'turquoise',
	'purple',
	'navy',
	'salmon',
	'forestGreen',
	'olive',
	'chocolate',
] as Array<ExtraUserColour>;

export const IMAGE_TASK_IDS = [
	'Image-Photorealistic',
	'Image-Artistic',
	'Image-Oil',
	'Image-Line',
	'Image-Anime',
	'Image-Watercolors',
	'Image-Architecture',
	'Image-Portrait',
] as const;

export type ImageTaskId = (typeof IMAGE_TASK_IDS)[number];

export const VIDEO_TASK_IDS = [
	'Video-PushIn',
	'Video-PullOut',
	'Video-ZoomIn',
	'Video-ZoomOut',
	'Video-PanLeft',
	'Video-PanRight',
	'Video-TiltUp',
	'Video-TiltDown',
	'Video-TruckLeft',
	'Video-TruckRight',
	'Video-PedestalUp',
	'Video-PedestalDown',
] as const;

export type VideoTaskId = (typeof VIDEO_TASK_IDS)[number];

export const SUB_TASK_IDS = [
	'PESTEL-P',
	'PESTEL-E',
	'PESTEL-S',
	'PESTEL-T',
	'PESTEL-Env',
	'PESTEL-L',
	'SWOT-S',
	'SWOT-W',
	'SWOT-O',
	'SWOT-T',
	'SCAMPER-S',
	'SCAMPER-C',
	'SCAMPER-A',
	'SCAMPER-M',
	'SCAMPER-P',
	'SCAMPER-E',
	'SCAMPER-R',
	...IMAGE_TASK_IDS,
	...VIDEO_TASK_IDS,
] as const;
export type SubTaskId = (typeof SUB_TASK_IDS)[number];
type Task = {
	name: string;
	label?: string;
	prompt?: string;
	likeType?: LikedItem['type'];
	options?: Record<
		SubTaskId,
		{
			name: string;
			optionName: string;
			requiresAdvanced?: boolean;
			likeType?: LikedItem['type'];
		}
	>;
};

export const SYNTHESISE_AI_TASKS = {
	Summary: {
		name: 'Write a SUMMARY',
		likeType: 'summary',
	} as Task,
	Extract: {
		name: 'EXTRACT the essence',
		likeType: 'extract',
	} as Task,
	Unite: {
		name: 'Find a COMMON IDEA uniting several topics',
		likeType: 'unite',
	} as Task,
	Tagline: {
		name: 'Create 5 TAGLINEs',
		likeType: 'taglines',
		label: 'Taglines',
	} as Task,
	// add post for linkedin post.
	Post: {
		name: 'Create a Linkedin Post',
		likeType: 'linkedinpost',
		label: 'Post',
	} as Task,
	Strategy: {
		label: 'Brand Strategy',
		name: 'Propose STRATEGY for the brand',
		likeType: 'brand-strategy',
	} as Task,
	Synopsis: {
		label: 'Story Synopsis',
		name: 'Write a STORY SYNOPSIS (up to one page)',
		likeType: 'synopsis',
	} as Task,
	Brief: {
		label: 'Product Brief',
		name: 'Write PRODUCT BRIEF (one page long)',
		likeType: 'product-brief',
	} as Task,
	TechnicalSpec: {
		label: 'Technical Spec',
		name: 'Write a structured TECHNICAL SPECIFICATION',
		likeType: 'technical-spec',
	} as Task,
	'Prod-ideas': {
		label: 'Product Idea',
		name: 'Come up with a PRODUCT IDEA',
		likeType: 'product-idea',
	} as Task,
	Audience: {
		label: 'Target Audiences',
		name: 'Propose 3-4 TARGET AUDIENCES',
		likeType: 'audience',
	} as Task,
	Game: {
		name: 'Create a GAME',
		likeType: 'game',
	} as Task,
	Brainstorming: {
		name: 'BRAINSTORM',
		likeType: 'brainstorm',
	} as Task,
	PESTEL: {
		name: 'Apply the PESTEL framework to analyze the impact of macro external factors',
		prompt: 'Which of the following external factors would you like to analyze now?',
		options: {
			'PESTEL-P': {
				name: 'Political perspective',
				optionName: 'P',
				likeType: 'pestel-p',
			},
			'PESTEL-E': {
				name: 'Economic impact',
				optionName: 'E',
				likeType: 'pestel-e',
			},
			'PESTEL-S': {
				name: 'Social factors',
				optionName: 'S',
				likeType: 'pestel-s',
			},
			'PESTEL-T': {
				name: 'Technological point of view',
				optionName: 'T',
				likeType: 'pestel-t',
			},
			'PESTEL-Env': {
				name: 'Environmental factors',
				optionName: 'Env',
				likeType: 'pestel-env',
			},
			'PESTEL-L': {
				name: 'Legal aspects',
				optionName: 'L',
				likeType: 'pestel-l',
			},
		},
	} as Task,
	SCAMPER: {
		name: 'Employ the SCAMPER technique within a creative framework',
		prompt: 'What aspect of improvement would you like to implement now?',
		options: {
			'SCAMPER-S': {
				name: 'Substitute',
				optionName: 'S',
				likeType: 'scamper-s',
			},
			'SCAMPER-C': {
				name: 'Combine',
				optionName: 'C',
				likeType: 'scamper-c',
			},
			'SCAMPER-A': {
				name: 'Adapt',
				optionName: 'A',
				likeType: 'scamper-a',
			},
			'SCAMPER-M': {
				name: 'Modify',
				optionName: 'M',
				likeType: 'scamper-m',
			},
			'SCAMPER-P': {
				name: 'Put to another use',
				optionName: 'P',
				likeType: 'scamper-p',
			},
			'SCAMPER-E': {
				name: 'Eliminate',
				optionName: 'E',
				likeType: 'scamper-e',
			},
			'SCAMPER-R': {
				name: 'Reverse',
				optionName: 'R',
				likeType: 'scamper-r',
			},
		},
	} as Task,
	SWOT: {
		name: 'SWOT analysis focusses on strategies for internal actions',
		prompt: 'What situational assessment aspect would you like to analyse now?',
		options: {
			'SWOT-S': {
				name: 'Strengths',
				optionName: 'S',
				likeType: 'swot-s',
			},
			'SWOT-W': {
				name: 'Weaknesses',
				optionName: 'W',
				likeType: 'swot-w',
			},
			'SWOT-O': {
				name: 'Opportunities',
				optionName: 'O',
				likeType: 'swot-o',
			},
			'SWOT-T': {
				name: 'Threats',
				optionName: 'T',
				likeType: 'swot-t',
			},
		},
	} as Task,
	Image: {
		name: 'Create an IMAGE',
		prompt: 'What kind of image would you like to create?',
		options: {
			'Image-Artistic': {
				name: 'A varied creative direction selected for the brief.',
				optionName: 'Creative mix',
			},
			'Image-Photorealistic': {
				name: 'Editorial, cinematic photography with natural materials and lighting.',
				optionName: 'Editorial photography',
				requiresAdvanced: true,
			},
			'Image-Oil': {
				name: 'Rich, tactile painterly artwork.',
				optionName: 'Oil painting',
				requiresAdvanced: true,
			},
			'Image-Line': {
				name: 'Clean graphic linework and considered negative space.',
				optionName: 'Graphic line art',
				requiresAdvanced: true,
			},
			'Image-Anime': {
				name: 'Contemporary 2D anime or manga art direction.',
				optionName: 'Anime illustration',
				requiresAdvanced: true,
			},
			'Image-Watercolors': {
				name: 'Layered watercolour or aquarelle illustration.',
				optionName: 'Watercolour illustration',
				requiresAdvanced: true,
			},
			'Image-Architecture': {
				name: 'Precise isometric architectural visualisation.',
				optionName: 'Isometric architecture',
				requiresAdvanced: true,
			},
			'Image-Portrait': {
				name: 'A composed, expressive character portrait.',
				optionName: 'Character portrait',
				requiresAdvanced: true,
			},
		},
	} as Task,
	Video: {
		name: 'Create a VIDEO',
		prompt: 'What kind of video would you like to create?',
		options: {
			'Video-PushIn': {
				requiresAdvanced: true,
				optionName: 'Push in',
				name: 'The camera moves horizontally forward',
			},
			'Video-PullOut': {
				requiresAdvanced: true,
				optionName: 'Pull out',
				name: 'The camera moves horizontally backward',
			},
			'Video-ZoomIn': {
				requiresAdvanced: true,
				optionName: 'Zoom in',
				name: 'The video frame enlarges proportionally',
			},
			'Video-ZoomOut': {
				requiresAdvanced: true,
				optionName: 'Zoom out',
				name: 'The video frame shrinks proportionally',
			},
			'Video-PanLeft': {
				requiresAdvanced: true,
				optionName: 'Pan left',
				name: 'The camera remains stationary while the shooting angle pans horizontally to the left',
			},
			'Video-PanRight': {
				requiresAdvanced: true,
				optionName: 'Pan right',
				name: 'The camera remains stationary while the shooting angle pans horizontally to the right',
			},
			'Video-TiltUp': {
				requiresAdvanced: true,
				optionName: 'Tilt up',
				name: 'The camera remains stationary while the shooting angle tilts vertically upward',
			},
			'Video-TiltDown': {
				requiresAdvanced: true,
				optionName: 'Tilt down',
				name: 'The camera remains stationary while the shooting angle tilts vertically downward',
			},
			'Video-TruckLeft': {
				requiresAdvanced: true,
				optionName: 'Truck left',
				name: 'The camera remains stationary while the shooting angle pans horizontally to the left',
			},
			'Video-TruckRight': {
				requiresAdvanced: true,
				optionName: 'Truck right',
				name: 'The camera remains stationary while the shooting angle pans horizontally to the right',
			},
			'Video-PedestalUp': {
				requiresAdvanced: true,
				optionName: 'Pedestal up',
				name: 'The camera remains stationary while the shooting angle tilts vertically upward',
			},
			'Video-PedestalDown': {
				requiresAdvanced: true,
				optionName: 'Pedestal down',
				name: 'The camera remains stationary while the shooting angle tilts vertically downward',
			},
		},
	} as Task,
};

export const EMPATHISE_COLOR_MAP: {
	[key: string]: string; // Flexible string indexing
} = {
	positive: 'GREEN',
	Positive: 'GREEN',
	negative: 'RED',
	Negative: 'RED',
	emotional: 'FUSCHIA',
	Emotional: 'FUSCHIA',
	cooccurrences: 'BLUE',
	Cooccurrences: 'BLUE',
	cooccurrence: 'BLUE',
	Cooccurrence: 'BLUE',
	'co-occurrences': 'BLUE',
	'Co-occurrences': 'BLUE',
	'co-occurrence': 'BLUE',
	'Co-occurrence': 'BLUE',
};

export const PUBLIC_DOMAINS = [
	'gmail.com',
	'yahoo.com',
	'outlook.com',
	'hotmail.com',
	'live.com',
	'aol.com',
	'icloud.com',
	'mail.com',
	'gmx.com',
	'protonmail.com',
	'zoho.com',
	'yandex.com',
	'me.com',
	'msn.com',
	'fastmail.com',
	'hushmail.com',
	'rediffmail.com',
	'comcast.net',
	'sbcglobal.net',
	'verizon.net',
	'att.net',
	'btinternet.com',
	'earthlink.net',
	'qq.com',
	'sina.com',
	'126.com',
	'163.com',
	'lycos.com',
	'aim.com',
	'rambler.ru',
];
